import React, { useState, useEffect } from 'react';

import { CheckCircle, AlertCircle } from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { useProtocols, useProtocolMutations } from '../../../hooks/useProtocols';

import { useProtocolCategories } from '../../../hooks/useNomenclatures';

import { useNotifications } from '../../NotificationSystem';

import './SetupWizardSteps.css';



const Step7Protocols = ({ stepData, onComplete, onValidation, userData, userName, structure }) => {

  const { notify } = useNotifications();

  const { t } = useTranslation('setup');

  const [loading, setLoading] = useState(false);

  const [protocols, setProtocols] = useState([]);

  const organizationId = structure?.organizations?.[0]?.id || null;

  const { protocols: existingProtocols, loading: protocolsLoading } = useProtocols({});

  const { createProtocol } = useProtocolMutations();

  const { categories } = useProtocolCategories();



  useEffect(() => {

    if (existingProtocols) {

      setProtocols(existingProtocols);

      if (existingProtocols.length >= 3) {

        onComplete({ protocols: existingProtocols.length, requirementsMet: true });

      }

    }

  }, [existingProtocols]);



  const makeStep = (idBase, order, stepKey, prefix, estimatedDuration) => ({

    id: `step_${idBase}_${order}`,

    order,

    name: t(`${prefix}.steps.${stepKey}.name`),

    description: t(`${prefix}.steps.${stepKey}.description`),

    estimatedDuration,

    actions: [

      {

        id: `action_${idBase}_${order}`,

        type: 'action',

        description: t(`${prefix}.steps.${stepKey}.action`),

        order: 1

      }

    ]

  });



  const handleCreateBasicProtocols = async () => {

    if (!organizationId) {

      notify.error(t('step7.notifications.missingOrganization'), t('step7.notifications.missingOrganizationTitle'));

      return;

    }



    setLoading(true);

    try {

      const userId = userData?.uid || 'system';

      const idBase = Date.now();

      const ex1 = 'step7.defaultProtocols.example1';

      const ex2 = 'step7.defaultProtocols.example2';

      const ex3 = 'step7.defaultProtocols.example3';



      const basicProtocols = [

        {

          name: t(`${ex1}.name`),

          shortName: 'EX1',

          protocol_code: 'EXAMPLE-001',

          category: 'PC',

          description: t(`${ex1}.description`),

          version: '1.0',

          complianceTarget: 95,

          frequency: {

            type: 'scheduled',

            duration: 30,

            interval: 'per_shift'

          },

          isCritical: true,

          requiresDocumentation: true,

          steps: [

            makeStep(idBase, 1, 'step1', ex1, 1),

            makeStep(idBase, 2, 'step2', ex1, 2),

            makeStep(idBase, 3, 'step3', ex1, 20),

            makeStep(idBase, 4, 'step4', ex1, 2)

          ],

          totalEstimatedDuration: 25,

          status: 'Active',

          is_active: true

        },

        {

          name: t(`${ex2}.name`),

          shortName: 'EX2',

          protocol_code: 'EXAMPLE-002',

          category: 'PC',

          description: t(`${ex2}.description`),

          version: '1.0',

          complianceTarget: 90,

          frequency: {

            type: 'scheduled',

            duration: 60,

            interval: 'daily'

          },

          isCritical: true,

          requiresDocumentation: true,

          steps: [

            makeStep(idBase, 5, 'step1', ex2, 5),

            makeStep(idBase, 6, 'step2', ex2, 40),

            makeStep(idBase, 7, 'step3', ex2, 10)

          ],

          totalEstimatedDuration: 55,

          status: 'Active',

          is_active: true

        },

        {

          name: t(`${ex3}.name`),

          shortName: 'EX3',

          protocol_code: 'EXAMPLE-003',

          category: 'PC',

          description: t(`${ex3}.description`),

          version: '1.0',

          complianceTarget: 100,

          frequency: {

            type: 'event-based',

            trigger: 'patient_contact'

          },

          isCritical: true,

          requiresDocumentation: true,

          steps: [

            makeStep(idBase, 8, 'step1', ex3, 2),

            makeStep(idBase, 9, 'step2', ex3, 5),

            makeStep(idBase, 10, 'step3', ex3, 5)

          ],

          totalEstimatedDuration: 12,

          status: 'Active',

          is_active: true

        }

      ];



      for (const protocol of basicProtocols) {

        await createProtocol(protocol, userId);

      }



      notify.success(t('step7.notifications.createdSuccess'), t('step7.notifications.createdSuccessTitle'));

      onComplete({ protocols: 3, requirementsMet: true });

    } catch (error) {

      console.error('Error creating protocols:', error);

      notify.error(t('step7.notifications.createFailed'), t('step7.notifications.createFailedTitle'));

    } finally {

      setLoading(false);

    }

  };



  const requirements = {

    protocols: protocols.length >= 3

  };



  return (

    <div className="step-content">

      <div className="step-section">

        <h3>{t('step7.title')}</h3>

        <p>{t('step7.description')}</p>

      </div>



      {!organizationId && (

        <div className="error-message" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '6px' }}>

          {t('step7.completeStep1Warning')}

        </div>

      )}



      <div className="quick-setup-section">

        <div className="quick-setup-option" style={{ cursor: loading || !organizationId ? 'not-allowed' : 'pointer', opacity: loading || !organizationId ? 0.6 : 1 }}>

          <div style={{ textAlign: 'center' }}>

            <h3>{t('step7.quickSetupTitle')}</h3>

            <p>{t('step7.quickSetupDescription')}</p>

            <ul style={{ textAlign: 'left', marginTop: '12px', paddingLeft: '20px' }}>

              <li>{t('step7.protocolList.example1')}</li>

              <li>{t('step7.protocolList.example2')}</li>

              <li>{t('step7.protocolList.example3')}</li>

            </ul>

            <button

              type="button"

              className="btn btn-primary"

              onClick={handleCreateBasicProtocols}

              disabled={loading || !organizationId}

              style={{ marginTop: '16px' }}

            >

              {loading ? t('step7.creating') : t('step7.createBasicProtocols')}

            </button>

          </div>

        </div>

      </div>



      <div className="requirements-section" style={{ marginTop: '32px' }}>

        <h3>{t('step7.protocolsTitle')}</h3>

        <div className="requirements-list">

          <div className={`requirement-item ${requirements.protocols ? 'met' : 'pending'}`}>

            {requirements.protocols ? <CheckCircle size={20} color="#4caf50" /> : <AlertCircle size={20} color="#ff9800" />}

            <span>{t('step7.protocolsCount', { count: protocols.length })}</span>

          </div>

        </div>



        {requirements.protocols && (

          <div className="success-message" style={{ marginTop: '20px' }}>

            {t('step7.minimumMet')}

          </div>

        )}

      </div>



      <div className="manual-setup-section" style={{ marginTop: '32px' }}>

        <p style={{ color: '#666', fontStyle: 'italic' }}>

          {t('step7.manualSetupNote')}

        </p>

      </div>

    </div>

  );

};



export default Step7Protocols;

